import classNames from 'classnames';
import React, { memo, useMemo } from 'react';
import { Accordion, Nav } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { Link, matchPath, useLocation } from 'react-router-dom';

import { UiInnerNotSPALink } from 'src/components/UI/InnerNotSPALink';
import { FrontConfigurationFutureFlagsType } from 'src/models/FrontendConfiguration';
import {
  ROUTE_ADMINISTRATION_AFFILIATES,
  ROUTE_ADMINISTRATION_AFFILIATES_PAYMENTS,
  ROUTE_ADMINISTRATION_AUDIT_PAGE,
  ROUTE_ADMINISTRATION_PAYMENT_APP_PAGE,
  ROUTE_ADMINISTRATION_CAMPAIGNS_EXPORT_DONATIONS,
  ROUTE_ADMINISTRATION_CAMPAIGNS_FUNDREQUESTS,
  ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT,
  ROUTE_ADMINISTRATION_CAMPAIGNS_TRANSACTIONS,
  ROUTE_ADMINISTRATION_CONFIGURATION_FEES,
  ROUTE_ADMINISTRATION_CONFIGURATION_FUNDING,
  ROUTE_ADMINISTRATION_CONFIGURATION_PLEDGE_FUNDING,
  ROUTE_ADMINISTRATION_CONFIGURATION_PROMO_CODES,
  ROUTE_ADMINISTRATION_CONFIGURATION_RAFFLE_SWEEPS_TIP,
  ROUTE_ADMINISTRATION_CONFIGURATION_SUBSCRIPTION_PLANS,
  ROUTE_ADMINISTRATION_CONFIGURATION_USERS,
  ROUTE_ADMINISTRATION_ERROR_LOGS_PAGE,
  ROUTE_ADMINISTRATION_EXPERIENCE_TEMPLATES,
  ROUTE_ADMINISTRATION_MANAGED_CAMPAIGNS,
  ROUTE_ADMINISTRATION_IMPORT_DONATIONS,
  ROUTE_ADMINISTRATION_INTEGRATIONS_REST_API,
  ROUTE_ADMINISTRATION_INTEGRATIONS_SYSTEM,
  ROUTE_ADMINISTRATION_ORGANIZATIONS_MANAGEMENT,
  ROUTE_ADMINISTRATION_PARTNERS_MANAGEMENT,
  ROUTE_PARTNER_ADMINISTRATION_ADMINISTRATORS_LIST,
  ROUTE_PARTNER_ADMINISTRATION_API_LIST,
  ROUTE_PARTNER_ADMINISTRATION_AUDIT_PAGE,
  ROUTE_PARTNER_ADMINISTRATION_CAMPAIGNS_MANAGEMENENT,
  ROUTE_PARTNER_ADMINISTRATION_EXPERIENCE_TEMPLATES_PAGE,
  ROUTE_PARTNER_ADMINISTRATION_FUNDING_PAGE,
  ROUTE_PARTNER_ADMINISTRATION_ORGANIZATIONS_LIST,
  ROUTE_PARTNER_ADMINISTRATION_TRANSACTIONS_LIST,
  ROUTE_PARTNER_ADMINISTRATION_USERS_LIST,
  ROUTE_PARTNER_ADMINISTRATION_WEBHOOKS_PAGE,
} from 'src/pages/Administration/administration';
import { getFlagByRoute } from 'src/routes';
import { deCommonCurrentPartnerSelector } from 'src/store/commonPart/selector';
import { featureFlagsSelector } from 'src/store/featureFlags/selectors';
import { scrollWindowToTop } from 'src/utils/DOM';
import { THelperType, useNsTHelper } from 'src/utils/i18n';

type MenuItemType = {
  url: string;
  title: string;
  activePaths?: string[];
  notSpa?: boolean;
};

type MenuType = { title: string; items: MenuItemType[] };

const checkActivePath = (url: string, pathname: string, activePaths?: string[]): boolean =>
  pathname === url.toLowerCase() || (activePaths ? !!matchPath(pathname, { path: activePaths, exact: true }) : false);

const MenuItem = memo(
  ({
    url,
    featureFlags,
    title,
    activePaths,
    pathname,
    notSpa,
  }: {
    url: string;
    title: string;
    featureFlags: FrontConfigurationFutureFlagsType;
    activePaths: string[] | undefined;
    pathname: string;
    notSpa: boolean;
  }) => {
    const featureFlagsValue: boolean | undefined = featureFlags[getFlagByRoute(url)];

    let showSpaLink = !!featureFlagsValue;

    if (notSpa) {
      showSpaLink = false;
    } else if (featureFlagsValue === undefined) {
      showSpaLink = true;
    }

    return showSpaLink ? (
      <Link
        key={url}
        to={url}
        className={classNames(`list-group-item`, {
          active: checkActivePath(url, pathname, activePaths),
        })}
        onClick={() => scrollWindowToTop()}
      >
        {title}
      </Link>
    ) : (
      <UiInnerNotSPALink
        key={url}
        className={classNames(`list-group-item no-spa`, {
          active: checkActivePath(url, pathname, activePaths),
        })}
        href={url}
      >
        {title}
      </UiInnerNotSPALink>
    );
  }
);

const ItemsMenu = memo(({ items, pathname }: { items: MenuItemType[]; pathname: string }) => {
  const featureFlags = useSelector(featureFlagsSelector);

  return (
    <>
      {items.map(({ url, title, activePaths, notSpa }) => (
        <MenuItem
          key={`${url}_${title}`}
          url={url}
          pathname={pathname}
          title={title}
          activePaths={activePaths}
          featureFlags={featureFlags}
          notSpa={notSpa || false}
        />
      ))}
    </>
  );
});

const Menu = ({ menu, pathname }: { menu: MenuType; pathname: string }) => (
  <Accordion defaultActiveKey="0" className="admin-menu">
    <div className="panel panel-default">
      <div className="panel-heading">
        <Accordion.Toggle as={Nav.Link} eventKey="0">
          {menu.title}
        </Accordion.Toggle>
      </div>
      <Accordion.Collapse eventKey="0" className="list-group">
        <ItemsMenu items={menu.items} pathname={pathname} />
      </Accordion.Collapse>
    </div>
  </Accordion>
);

const menuItems = (
  t: THelperType,
  {
    IsCampaignTemplateFunctionalityEnabled,
    PledgePaymentProcessingEnabled,
  }: {
    IsCampaignTemplateFunctionalityEnabled: boolean;
    PledgePaymentProcessingEnabled: boolean;
  }
): MenuType[] => [
  {
    title: t('campaigns.title'),
    items: (() => {
      const arr = [
        {
          url: ROUTE_ADMINISTRATION_CAMPAIGNS_MANAGEMENT,
          title: t('campaigns.items.management'),
        },
        {
          url: ROUTE_ADMINISTRATION_CAMPAIGNS_FUNDREQUESTS,
          title: t('campaigns.items.fund'),
        },
        {
          url: ROUTE_ADMINISTRATION_AFFILIATES,
          title: t('campaigns.items.affiliates'),
        },
        {
          url: ROUTE_ADMINISTRATION_AFFILIATES_PAYMENTS,
          title: t('campaigns.items.affiliatePayments'),
        },
        {
          url: ROUTE_ADMINISTRATION_CAMPAIGNS_TRANSACTIONS,
          title: t('campaigns.items.transactions'),
        },
        {
          url: ROUTE_ADMINISTRATION_IMPORT_DONATIONS,
          title: t('campaigns.items.importDonations'),
        },
        {
          url: ROUTE_ADMINISTRATION_CAMPAIGNS_EXPORT_DONATIONS,
          title: t('campaigns.items.exportDonations'),
        },
      ];

      if (IsCampaignTemplateFunctionalityEnabled) {
        arr.push({
          url: ROUTE_ADMINISTRATION_EXPERIENCE_TEMPLATES,
          title: t('campaigns.items.experienceTemplates'),
        });
      }

      arr.push({
        url: ROUTE_ADMINISTRATION_MANAGED_CAMPAIGNS,
        title: t('campaigns.items.managedCampaigns'),
      });

      return arr;
    })(),
  },
  {
    title: t('organizations.title'),
    items: [
      {
        url: ROUTE_ADMINISTRATION_ORGANIZATIONS_MANAGEMENT,
        title: t('organizations.items.management'),
      },
      /** #17550 open this page on an organization management page */
      // {
      //   url: FlaggedRoutes.ROUTE_ADMINISTRATION_ORGANIZATIONS_ADMINISTRATORS.path,
      //   title: t('organizations.items.administrators'),
      //   activePaths: [FlaggedRoutes.ROUTE_ADMINISTRATION_ORGANIZATIONS_ADMINISTRATORS_PERMANENT_URL.path],
      // },
    ],
  },
  {
    title: t('partners.title'),
    items: [
      {
        url: ROUTE_ADMINISTRATION_PARTNERS_MANAGEMENT,
        title: t('partners.items.management'),
      },
    ],
  },
  {
    title: t('configuration.title'),
    items: [
      {
        url: ROUTE_ADMINISTRATION_CONFIGURATION_USERS,
        title: t('configuration.items.users'),
      },
      {
        url: ROUTE_ADMINISTRATION_CONFIGURATION_SUBSCRIPTION_PLANS,
        title: t('configuration.items.subscriptionPlans'),
      },
      {
        url: ROUTE_ADMINISTRATION_CONFIGURATION_PROMO_CODES,
        title: t('configuration.items.promocodes'),
      },
      {
        url: ROUTE_ADMINISTRATION_CONFIGURATION_FEES,
        title: t('configuration.items.fees'),
      },
      {
        url: ROUTE_ADMINISTRATION_CONFIGURATION_RAFFLE_SWEEPS_TIP,
        title: t('configuration.items.defaultTipPercent'),
      },
      {
        url: ROUTE_ADMINISTRATION_CONFIGURATION_FUNDING,
        title: t('configuration.items.funding'),
      },
      PledgePaymentProcessingEnabled && {
        url: ROUTE_ADMINISTRATION_CONFIGURATION_PLEDGE_FUNDING,
        title: t('configuration.items.pledgeFunding'),
      },
      {
        url: ROUTE_ADMINISTRATION_ERROR_LOGS_PAGE,
        title: t('configuration.items.errorLogs'),
      },
      {
        url: ROUTE_ADMINISTRATION_AUDIT_PAGE,
        title: t('configuration.items.auditTrail'),
      },
      {
        url: ROUTE_ADMINISTRATION_PAYMENT_APP_PAGE,
        title: t('configuration.items.paymentApplication'), 
      },
    ].filter(Boolean) as MenuItemType[], // conditional items
  },
  {
    title: t('integrations.title'),
    items: [
      {
        url: ROUTE_ADMINISTRATION_INTEGRATIONS_SYSTEM,
        title: t('integrations.items.clientApi'),
      },
      {
        url: ROUTE_ADMINISTRATION_INTEGRATIONS_REST_API,
        title: t('integrations.items.restApi'),
      },
    ],
  },
];

const partnerMenuItems = (
  t: THelperType,
  {
    IsPartnerLevelFundingEnabled,
    IsCampaignTemplateFunctionalityEnabled,
  }: {
    IsPartnerLevelFundingEnabled: boolean;
    IsCampaignTemplateFunctionalityEnabled: boolean;
  }
): MenuType[] => {
  const tHelper = (str: string) => t(`partner.items.${str}`);

  const items: MenuItemType[] = [
    {
      url: ROUTE_PARTNER_ADMINISTRATION_CAMPAIGNS_MANAGEMENENT,
      title: tHelper('campaigns'),
    },
    {
      url: ROUTE_PARTNER_ADMINISTRATION_ORGANIZATIONS_LIST,
      title: tHelper('organizations'),
    },
    {
      url: ROUTE_PARTNER_ADMINISTRATION_TRANSACTIONS_LIST,
      title: tHelper('transactions'),
    },
    {
      url: ROUTE_PARTNER_ADMINISTRATION_USERS_LIST,
      title: tHelper('users'),
    },
    {
      url: ROUTE_PARTNER_ADMINISTRATION_ADMINISTRATORS_LIST,
      title: tHelper('administrators'),
    },
    {
      url: ROUTE_PARTNER_ADMINISTRATION_API_LIST,
      title: tHelper('api'),
    },
    {
      url: ROUTE_PARTNER_ADMINISTRATION_WEBHOOKS_PAGE,
      title: tHelper('webhook'),
    },
    {
      url: ROUTE_PARTNER_ADMINISTRATION_AUDIT_PAGE,
      title: tHelper('auditTrail'),
    },
  ];

  if (IsPartnerLevelFundingEnabled) {
    items.push({
      url: ROUTE_PARTNER_ADMINISTRATION_FUNDING_PAGE,
      title: tHelper('funding'),
    });
  }

  if (IsCampaignTemplateFunctionalityEnabled) {
    items.push({
      url: ROUTE_PARTNER_ADMINISTRATION_EXPERIENCE_TEMPLATES_PAGE,
      title: tHelper('experienceTemplates'),
    });
  }

  return [
    {
      title: t('partner.title'),
      items,
    },
  ];
};

type AdministrationMenuProps = { partner: boolean };

export const AdministrationMenu: React.FC<AdministrationMenuProps> = memo(({ partner }: AdministrationMenuProps) => {
  const { pathname } = useLocation();

  const tHelper = useNsTHelper('administration.menu');

  const { IsPartnerLevelFundingEnabled, IsCampaignTemplateFunctionalityEnabled, PledgePaymentProcessingEnabled } =
    useSelector(deCommonCurrentPartnerSelector);

  const translatedMenu = useMemo(
    () =>
      partner
        ? partnerMenuItems(tHelper, {
            IsPartnerLevelFundingEnabled,
            IsCampaignTemplateFunctionalityEnabled,
          })
        : menuItems(tHelper, {
            IsCampaignTemplateFunctionalityEnabled,
            PledgePaymentProcessingEnabled,
          }),
    [
      partner,
      tHelper,
      IsPartnerLevelFundingEnabled,
      IsCampaignTemplateFunctionalityEnabled,
      PledgePaymentProcessingEnabled,
    ]
  );

  return (
    <>
      {translatedMenu.map((menu) => (
        <Menu key={menu.title} menu={menu} pathname={pathname.toLowerCase()} />
      ))}
    </>
  );
});
